import { useEffect, useState } from "react";
import { Moon, Shield, Loader2, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import HUDFrame from "@/components/HUDFrame";

const dayLabel = { mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun" };
const dayIdx = { mon: 1, tue: 2, wed: 3, thu: 4, fri: 5, sat: 6, sun: 0 };

const minutesToLabel = (min) => {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const ampm = h >= 12 ? "PM" : "AM";
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, "0")} ${ampm}`;
};

// Return the next Date matching the given weekday key + start_min (mins-from-midnight)
function nextOccurrenceISO(weekdayKey, startMin) {
  const target = dayIdx[weekdayKey];
  const now = new Date();
  const days = (target - now.getDay() + 7) % 7 || 7;
  const dt = new Date(now.getFullYear(), now.getMonth(), now.getDate() + days);
  dt.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0);
  return dt.toISOString();
}

export default function Nest() {
  const [profile, setProfile] = useState(null);
  const [signals, setSignals] = useState(null);
  const [calStatus, setCalStatus] = useState(null);
  const [err, setErr] = useState(null);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get("/profile"), api.get("/signals"), api.get("/calendar/status")])
      .then(([p, s, c]) => {
        if (cancelled) return;
        setProfile(p.data);
        setSignals(s.data);
        setCalStatus(c.data);
      })
      .catch((e) => !cancelled && setErr(e?.response?.data?.detail || "Nest unavailable."));
    return () => {
      cancelled = true;
    };
  }, []);

  const defend = async (win, dayKey) => {
    const key = `${win.window_id}-${dayKey}`;
    setBusyKey(key);
    try {
      const startIso = nextOccurrenceISO(dayKey, win.start_min);
      const { data } = await api.post("/calendar/events/create", {
        summary: `${win.label} · defended window`,
        start: startIso,
        duration_min: win.duration_min,
        category: "defended",
        description: "Life OS · defended recovery window. Automatically respected by the tower.",
      });
      toast.success("Defended on your calendar.", {
        action: data.html_link ? { label: "Open", onClick: () => window.open(data.html_link, "_blank") } : undefined,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not defend on calendar.");
    } finally {
      setBusyKey(null);
    }
  };

  if (err) return <div className="mx-auto max-w-3xl px-6 py-16 text-mist" data-testid="nest-error">{String(err)}</div>;
  if (!profile || !signals)
    return (
      <div className="mx-auto flex max-w-3xl items-center gap-3 px-6 py-16 font-mono text-[11px] uppercase tracking-[0.22em] text-mist" data-testid="nest-loading">
        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} />
        Reading defended reserves…
      </div>
    );

  const windows = profile.recovery_windows || [];
  const recoveryPct = signals.recovery_margin;
  const recoveryStatus = signals.recovery_status;
  const canWrite = !!calStatus?.can_write;

  return (
    <div data-testid="nest-page" className="mx-auto max-w-7xl px-6 py-10 md:px-8 md:py-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-teal">N-06 · Nest</p>
      <h1 className="mt-4 font-display text-4xl leading-[1.05] text-ink md:text-5xl">
        Protected space
        <br />
        <span className="italic text-mist">to breathe.</span>
      </h1>
      <p className="mt-5 max-w-xl text-base leading-relaxed text-mist">
        Recovery is a resource — treated the same way an airline defends fuel
        reserves. Everything below is treated as untouchable unless you say otherwise.
      </p>

      <div className="mt-10 grid gap-6 md:grid-cols-3">
        <HUDFrame className="bg-deep/40 p-8" tone="teal">
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            Recovery reserve
          </div>
          <div className="mt-4 flex items-baseline gap-2">
            <span className={`font-mono text-5xl ${recoveryStatus === "red" ? "text-signalRed" : recoveryStatus === "amber" ? "text-signalAmber" : "text-signalGreen"}`}>
              {signals.protected_hours}
            </span>
            <span className="font-mono text-sm text-mist">h protected</span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-mist">
            Across {windows.reduce((a, w) => a + (w.days?.length || 0), 0)} weekly slot{windows.reduce((a, w) => a + (w.days?.length || 0), 0) === 1 ? "" : "s"}.
          </p>
        </HUDFrame>

        <HUDFrame className="bg-deep/40 p-8" tone={recoveryStatus === "red" ? "amber" : "teal"}>
          <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            Reserve status
          </div>
          <div className="mt-4 flex items-baseline gap-2">
            <span className={`font-mono text-5xl ${recoveryStatus === "red" ? "text-signalRed" : recoveryStatus === "amber" ? "text-signalAmber" : "text-signalGreen"}`}>
              {recoveryPct}%
            </span>
            <span className="font-mono text-sm text-mist">of weekly target</span>
          </div>
          <p className="mt-4 text-sm leading-relaxed text-mist">
            {recoveryStatus === "red"
              ? "Below defended minimum — add a window before adding load."
              : recoveryStatus === "amber"
                ? "Below comfort line — consider one more defended window."
                : "Above comfort line — you have room to absorb small events."}
          </p>
        </HUDFrame>

        <HUDFrame className="bg-deep/40 p-8" tone="teal">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            <Shield className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            Boundary
          </div>
          <p className="mt-4 font-display text-xl leading-snug text-ink" data-testid="nest-boundary">
            {windows[0]
              ? `${windows[0].label} — ${minutesToLabel(windows[0].start_min)} for ${(windows[0].duration_min / 60).toFixed(1)}h.`
              : "No windows defended yet."}
          </p>
          <p className="mt-3 text-sm leading-relaxed text-mist">
            Runway will block new commitments in defended windows unless you override.
          </p>
        </HUDFrame>
      </div>

      <HUDFrame className="mt-10 bg-deep/40 p-8" tone="teal">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.24em] text-mist">
            <Moon className="h-3.5 w-3.5 text-teal" strokeWidth={1.5} />
            Defended windows
          </div>
          {!canWrite && calStatus?.connected && (
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-signalAmber" data-testid="nest-reconnect-hint">
              Reconnect calendar to enable defend-on-calendar
            </span>
          )}
        </div>
        {windows.length === 0 ? (
          <p className="mt-6 text-sm text-mist">
            Add a recovery window during onboarding to defend time here.
          </p>
        ) : (
          <ul className="mt-6 divide-y divide-rule" data-testid="nest-windows">
            {windows.map((w) => (
              <li key={w.window_id} className="flex flex-wrap items-baseline gap-4 py-4">
                <span className="text-ink">{w.label}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-mist">
                  {w.days.map((d) => dayLabel[d]).join(" · ")}
                </span>
                <span className="ml-auto font-mono text-sm text-mist">
                  {minutesToLabel(w.start_min)} · {(w.duration_min / 60).toFixed(1)}h
                </span>
                <span className="border border-signalGreen/50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.2em] text-signalGreen">
                  held
                </span>
                {canWrite && w.days?.[0] && (
                  <button
                    onClick={() => defend(w, w.days[0])}
                    disabled={busyKey === `${w.window_id}-${w.days[0]}`}
                    data-testid={`nest-defend-${w.window_id}`}
                    className="inline-flex items-center gap-1.5 border border-rule bg-obsidian px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-mist transition-colors duration-300 hover:border-teal hover:text-ink disabled:opacity-50"
                    title={`Defend the next ${dayLabel[w.days[0]]} on your Google Calendar`}
                  >
                    <CalendarPlus className="h-3 w-3" strokeWidth={1.5} />
                    Defend this window
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </HUDFrame>
    </div>
  );
}
